import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BsBell, BsBellFill } from 'react-icons/bs';
import { FaBell } from 'react-icons/fa';
import { get, post } from '../../services/back.service';
import {
  markAllRead,
  markRead,
  openDetail,
  setItems,
} from '../../reducers/notificationsSlice';

const formatFecha = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return '';
  }
};

/**
 * Campana de notificaciones compartida por las dos versiones de UI.
 *
 * - Badge: contador de no leídas (`state.notifications.unreadCount`, que
 *   alimenta el snapshot y los eventos en vivo del WebSocket).
 * - Al abrir hace fetch al backend (la base manda: corrige contador y listado
 *   si algo se perdió o se leyó en otro dispositivo).
 * - Clic en un item: marcado optimista + `POST read`. Después navega a `link`
 *   (agendas) o abre el modal de detalle (anuncios). Si el POST falla no se
 *   rompe nada: la próxima apertura re-sincroniza.
 *
 * Variants: `v2` (por defecto, FaBell, botón `.v2-notify-btn`) y `v1`
 * (BsBell/BsBellFill, botón `.bell-btn`).
 */
export default function NotificationBell({ variant = 'v2' }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const unreadCount = useSelector((state) => state.notifications.unreadCount);
  const items = useSelector((state) => state.notifications.items);
  const [open, setOpen] = useState(false);
  const raiz = useRef(null);

  const badge = unreadCount > 9 ? '9+' : unreadCount;

  // Clic fuera del panel: lo cierra.
  useEffect(() => {
    if (!open) return undefined;
    const onFuera = (event) => {
      if (raiz.current && !raiz.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onFuera);
    return () => document.removeEventListener('mousedown', onFuera);
  }, [open]);

  const abrir = async () => {
    const siguiente = !open;
    setOpen(siguiente);
    if (!siguiente) return;

    const respuesta = await get('/notify/?limit=15');
    if (respuesta && respuesta.success) {
      dispatch(setItems({
        results: respuesta.data.results,
        unreadCount: respuesta.data.unread_count,
      }));
    }
  };

  const marcarLeida = (item) => {
    // Optimista: la UI responde al instante; si el POST falla, la próxima
    // apertura de la campana vuelve a pedir la verdad del servidor.
    dispatch(markRead({ id: item.id, readAt: new Date().toISOString() }));
    setOpen(false);
    if (item.kind === 'announcement') {
      // Los anuncios de administrador no navegan: muestran todo su detalle.
      dispatch(openDetail(item));
    } else if (item.link) {
      navigate(item.link);
    }
    post(`/notify/${item.id}/read`).catch(() => {});
  };

  const marcarTodas = () => {
    dispatch(markAllRead(new Date().toISOString()));
    post('/notify/read-all').catch(() => {});
  };

  const Icono = variant === 'v1'
    ? (unreadCount > 0 ? BsBellFill : BsBell)
    : FaBell;

  return (
    <div
      className={`bell bell-${variant}`}
      ref={raiz}
      data-testid="bell"
      data-variant={variant}
    >
      <button
        type="button"
        className={variant === 'v2' ? 'v2-notify-btn' : 'bell-btn'}
        onClick={abrir}
        aria-label="Notificaciones"
        title="Notificaciones"
        data-testid="bell-button"
      >
        <Icono />
        {unreadCount > 0 && (
          <span
            className="bell-badge"
            role="status"
            aria-label={`${badge} notificaciones sin leer`}
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div className={`bell-panel bell-panel-${variant}`} role="dialog" aria-label="Notificaciones">
          <div className="bell-panel-header">
            <strong>Notificaciones</strong>
            <button
              type="button"
              className="bell-close"
              aria-label="Cerrar"
              onClick={() => setOpen(false)}
            >
              &times;
            </button>
          </div>

          <div className="bell-panel-list">
            {items.length === 0 && (
              <p className="bell-empty">Sin notificaciones todavía</p>
            )}
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`bell-item${item.read_at ? '' : ' bell-item-unread'}`}
                onClick={() => marcarLeida(item)}
              >
                <span className="bell-item-title">{item.title}</span>
                <span className="bell-item-message">{item.message}</span>
                <span className="bell-item-date">{formatFecha(item.created)}</span>
              </button>
            ))}
          </div>

          <div className="bell-panel-footer">
            <button
              type="button"
              className="bell-mark-all"
              onClick={marcarTodas}
              disabled={unreadCount === 0}
            >
              Marcar todas como leídas
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
